// Persona editor — full-page form.
//
// V1 create/edit surface for a persona's attributes. Per spec:
//   1. Identity (name / sector / revenue_band / employee_band / dept size)
//   2. Geography & jurisdiction
//   3. GC profile (years / background)
//   4. Maturity per domain — 5-level segmented control per D1..D8
//   5. Business context narrative — free text, verbatim into prompt
//
// Route: /admin/knowledge/personae/new (create) + /admin/knowledge/personae/:id (edit)

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button } from '@gc-coach/design-system';
import type {
  Domain,
  MaturityLevel,
  Persona,
  PersonaAttributes,
  PersonaBackground,
  PersonaMaturityByDomain,
  PersonaYearsExperience,
} from '@gc-coach/shared';
import {
  ALL_DOMAINS,
  ALL_MATURITY_LEVELS,
  ALL_PERSONA_BACKGROUNDS,
} from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { useI18n } from '../i18n/index.js';
import { createPersona, getPersona, patchPersona } from '../api/endpoints.js';

const YEARS: ReadonlyArray<PersonaYearsExperience> = ['0-3', '4-7', '8-15', '15+'];
const DEFAULT_MATURITY: PersonaMaturityByDomain = {
  D1_strategy_org: 'initial',
  D2_external_counsel: 'initial',
  D3_legal_ops_tech: 'initial',
  D4_team_dev: 'initial',
  D5_kpis_performance: 'initial',
  D6_budget_finance: 'initial',
  D7_stakeholder_comms: 'initial',
  D8_governance_risk: 'initial',
};

export function PersonaEditor() {
  const { id } = useParams<{ id: string }>();
  const { t } = useI18n();
  const navigate = useNavigate();
  const isCreate = !id || id === 'new';

  const [entity, setEntity] = useState<Persona | null>(null);
  const [attrs, setAttrs] = useState<PersonaAttributes>({
    name: '',
    maturity_per_domain: { ...DEFAULT_MATURITY },
  });
  const [isLoading, setIsLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isCreate || !id) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const result = await getPersona(id);
      if (cancelled) return;
      if (result.ok) {
        setEntity(result.data.persona);
        setAttrs(result.data.persona.attributes);
      } else {
        setError(t('personae.editorLoadError'));
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isCreate, t]);

  async function save() {
    setSaving(true);
    setError(null);
    const result = isCreate
      ? await createPersona({ attributes: attrs })
      : await patchPersona(id!, { attributes: attrs });
    setSaving(false);
    if (!result.ok) {
      setError(t('personae.editorSaveError'));
      return;
    }
    navigate(`/admin/knowledge/personae/${result.data.persona.id}`);
  }

  // Merges partial updates while dropping keys explicitly set to undefined
  // (exactOptionalPropertyTypes disallows an assigned undefined on optional
  // properties). Callers can clear a field by setting it to null via the
  // narrower helpers below.
  const patch = (update: Record<string, unknown>) =>
    setAttrs((a) => {
      const nextMap = { ...(a as unknown as Record<string, unknown>) };
      for (const [k, v] of Object.entries(update)) {
        if (v === undefined) delete nextMap[k];
        else nextMap[k] = v;
      }
      return nextMap as unknown as PersonaAttributes;
    });

  const patchMaturity = (domain: Domain, level: MaturityLevel) =>
    setAttrs((a) => ({
      ...a,
      maturity_per_domain: { ...a.maturity_per_domain, [domain]: level },
    }));

  return (
    <AdminShell
      activeKey="personae"
      breadcrumb={[
        t('personae.breadcrumbKnowledge'),
        t('personae.breadcrumbPersonae'),
        isCreate ? t('personae.editorCreateTitle') : (entity?.name ?? '…'),
      ]}
      state={isLoading ? { kind: 'loading' } : { kind: 'normal' }}
    >
      <PageHeader
        title={isCreate ? t('personae.editorCreateTitle') : (entity?.name ?? '…')}
        sub={isCreate ? t('personae.editorCreateSub') : t('personae.editorEditSub')}
        actions={
          <>
            <Button
              kind="ghost"
              onClick={() => navigate('/admin/knowledge/personae')}
            >
              {t('personae.cancelButton')}
            </Button>
            <Button
              kind="primary"
              onClick={save}
              disabled={saving || attrs.name.trim().length === 0}
            >
              {saving ? t('personae.savingButton') : t('personae.saveButton')}
            </Button>
          </>
        }
      />

      {error && (
        <div style={{ marginBottom: 12 }}>
          <Alert severity="danger">{error}</Alert>
        </div>
      )}

      <FieldGroup title={t('personae.groupIdentity')}>
        <Field label={t('personae.fieldName')}>
          <input
            type="text"
            value={attrs.name}
            onChange={(e) => patch({ name: e.target.value })}
            style={inputStyle}
            placeholder={t('personae.fieldNamePlaceholder')}
          />
        </Field>
        <FieldRow>
          <Field label={t('personae.fieldSector')}>
            <input
              type="text"
              value={attrs.sector ?? ''}
              onChange={(e) => patch({ sector: e.target.value })}
              style={inputStyle}
            />
          </Field>
          <Field label={t('personae.fieldRevenueBand')}>
            <input
              type="text"
              value={attrs.revenue_band ?? ''}
              onChange={(e) => patch({ revenue_band: e.target.value })}
              style={inputStyle}
              placeholder="€10-50M"
            />
          </Field>
        </FieldRow>
        <FieldRow>
          <Field label={t('personae.fieldEmployeeBand')}>
            <input
              type="text"
              value={attrs.employee_band ?? ''}
              onChange={(e) => patch({ employee_band: e.target.value })}
              style={inputStyle}
              placeholder="500-2000"
            />
          </Field>
          <Field label={t('personae.fieldLegalDeptSize')}>
            <input
              type="number"
              value={attrs.legal_dept_size ?? ''}
              onChange={(e) =>
                patch({
                  legal_dept_size: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              min={0}
              style={inputStyle}
            />
          </Field>
        </FieldRow>
      </FieldGroup>

      <FieldGroup title={t('personae.groupGeography')}>
        <FieldRow>
          <Field label={t('personae.fieldGeographies')}>
            <input
              type="text"
              value={(attrs.geographies ?? []).join(', ')}
              onChange={(e) =>
                patch({
                  geographies: e.target.value
                    .split(',')
                    .map((g) => g.trim())
                    .filter(Boolean),
                })
              }
              style={inputStyle}
              placeholder="FR, DE, IT"
            />
          </Field>
          <Field label={t('personae.fieldPrimaryJurisdiction')}>
            <input
              type="text"
              value={attrs.primary_jurisdiction ?? ''}
              onChange={(e) => patch({ primary_jurisdiction: e.target.value.toUpperCase() })}
              maxLength={2}
              style={{ ...inputStyle, width: 80, textTransform: 'uppercase' }}
              placeholder="FR"
            />
          </Field>
        </FieldRow>
      </FieldGroup>

      <FieldGroup title={t('personae.groupGCProfile')}>
        <FieldRow>
          <Field label={t('personae.fieldYearsExperience')}>
            <select
              value={attrs.years_experience ?? ''}
              onChange={(e) =>
                patch({
                  years_experience: (e.target.value || undefined) as
                    | PersonaYearsExperience
                    | undefined,
                })
              }
              style={inputStyle}
            >
              <option value="">—</option>
              {YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('personae.fieldBackground')}>
            <select
              value={attrs.background ?? ''}
              onChange={(e) =>
                patch({
                  background: (e.target.value || undefined) as
                    | PersonaBackground
                    | undefined,
                })
              }
              style={inputStyle}
            >
              <option value="">—</option>
              {ALL_PERSONA_BACKGROUNDS.map((b) => (
                <option key={b} value={b}>
                  {t(`personae.background_${b}` as const)}
                </option>
              ))}
            </select>
          </Field>
        </FieldRow>
      </FieldGroup>

      <FieldGroup title={t('personae.groupMaturity')}>
        <div style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 10 }}>
          {t('personae.maturityHelper')}
        </div>
        {ALL_DOMAINS.map((d) => (
          <div
            key={d}
            style={{
              display: 'grid',
              gridTemplateColumns: '200px 1fr',
              gap: 12,
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <label style={{ fontSize: 13 }}>{t(`personae.domain_${d}` as const)}</label>
            <div style={{ display: 'flex', gap: 4 }}>
              {ALL_MATURITY_LEVELS.map((level) => {
                const selected = attrs.maturity_per_domain[d] === level;
                return (
                  <button
                    key={level}
                    type="button"
                    onClick={() => patchMaturity(d, level)}
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      fontSize: 11,
                      border: '1px solid var(--border)',
                      borderRadius: 4,
                      background: selected ? 'var(--brand)' : 'transparent',
                      color: selected ? '#fff' : 'var(--ink1)',
                      cursor: 'pointer',
                      textTransform: 'capitalize',
                    }}
                  >
                    {t(`personae.maturity_${level}` as const)}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </FieldGroup>

      <FieldGroup title={t('personae.groupBusinessContext')}>
        <div style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 6 }}>
          {t('personae.contextHelper')}
        </div>
        <textarea
          value={attrs.business_context_narrative ?? ''}
          onChange={(e) => patch({ business_context_narrative: e.target.value })}
          style={{ ...inputStyle, minHeight: 140, resize: 'vertical' }}
          placeholder={t('personae.contextPlaceholder')}
        />
      </FieldGroup>
    </AdminShell>
  );
}

function FieldGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: 16,
        marginBottom: 16,
        background: 'var(--paper)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontFamily: 'var(--font-mono)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--ink3)',
          marginBottom: 12,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label
        style={{
          fontSize: 11,
          fontFamily: 'var(--font-mono)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--ink3)',
          display: 'block',
          marginBottom: 4,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function FieldRow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  fontSize: 13,
  background: 'var(--bg)',
  color: 'var(--ink1)',
};
